import React, { useState } from "react";

import Form from "ui/Form";
import { useRoutes } from "utils/routes";
import Avatar from "ui/Avatar";

type PreAuth = {
  clientName: string;
  clientUid: string;
  redirectUri: string;
  state: string;
  responseType: string;
  responseMode: string;
  scope: string;
  codeChallenge: string;
  codeChallengeMethod: string;
};

type Scope = {
  name: string;
  label: string;
};

type Organization = {
  id: number;
  name: string;
};

const HiddenPreAuthFields = ({ preAuth }: { preAuth: PreAuth }) => (
  <>
    <input type="hidden" name="client_id" value={preAuth.clientUid} />
    <input type="hidden" name="redirect_uri" value={preAuth.redirectUri} />
    <input type="hidden" name="state" value={preAuth.state} />
    <input type="hidden" name="response_type" value={preAuth.responseType} />
    <input type="hidden" name="response_mode" value={preAuth.responseMode} />
    <input type="hidden" name="scope" value={preAuth.scope} />
    <input type="hidden" name="code_challenge" value={preAuth.codeChallenge} />
    <input
      type="hidden"
      name="code_challenge_method"
      value={preAuth.codeChallengeMethod}
    />
  </>
);

type DoorkeeperAuthorizationProps = {
  preAuth: PreAuth;
  organizations: Organization[];
  grantedScopes: Scope[];
  optionalScopes: Scope[];
  userFullname: string;
  userEmail: string;
  userAvatarUrl: string;
};

const DoorkeeperAuthorization = ({
  preAuth,
  organizations,
  grantedScopes,
  optionalScopes,
  userFullname,
  userEmail,
  userAvatarUrl,
}: DoorkeeperAuthorizationProps) => {
  const { oauthAuthorization } = useRoutes();
  const [orgId, setOrgId] = useState(
    organizations.length === 1 ? organizations[0].id.toString() : "",
  );
  const hasOrg = orgId !== "";
  const [checkedOptional, setCheckedOptional] = useState<Set<string>>(
    () => new Set(),
  );
  // write_workbooks implies read: keep the two checkboxes in sync so the user
  // can see what they're actually granting.
  const toggleOptional = (name: string, checked: boolean) => {
    setCheckedOptional((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(name);
        if (name === "write_workbooks") {
          next.add("read");
        }
      } else {
        next.delete(name);
        if (name === "read") {
          next.delete("write_workbooks");
        }
      }
      return next;
    });
  };
  return (
    <div className="form-container !max-w-[480px]">
      <Form action={oauthAuthorization()} method="post" className="!p-0">
        <h1>Authorize {preAuth.clientName}</h1>
        <p>
          <strong>{preAuth.clientName}</strong> is requesting access to your
          pganalyze account.
        </p>
        <HiddenPreAuthFields preAuth={preAuth} />
        <div className="my-6">
          <div className="form-group">
            <label htmlFor="organization_id" className="control-label">
              Organization:
            </label>
            {organizations.length === 1 ? (
              <>
                <input
                  type="hidden"
                  name="organization_id"
                  value={organizations[0].id}
                />
                <p className="!m-0">{organizations[0].name}</p>
              </>
            ) : (
              <select
                name="organization_id"
                id="organization_id"
                required
                className="form-control"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
              >
                <option value="">Select an organization...</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <p className="text-sm text-[#595959]">
            Access will be limited to data within the{" "}
            {organizations.length === 1 ? "above" : "selected"} organization.
          </p>
        </div>
        {hasOrg && (grantedScopes.length > 0 || optionalScopes.length > 0) && (
          <>
            <div className="my-4 p-4 bg-[#f9fafb] rounded-md form-group">
              <p className="font-semibold mb-2">Permissions to grant:</p>
              {grantedScopes.map((scope) => (
                <label key={scope.name} className="block !font-normal">
                  <input type="checkbox" checked disabled className="!mr-2" />
                  <span className="font-semibold">{scope.name}</span>
                  {": "}
                  {scope.label}
                  <span className="font-semibold italic"> (required)</span>
                </label>
              ))}
              {optionalScopes.map((scope) => (
                <label key={scope.name} className="block !font-normal">
                  <input
                    type="checkbox"
                    name="optional_scopes[]"
                    value={scope.name}
                    checked={checkedOptional.has(scope.name)}
                    onChange={(e) =>
                      toggleOptional(scope.name, e.target.checked)
                    }
                    className="!mr-2"
                  />
                  <span className="font-semibold">{scope.name}</span>
                  {": "}
                  {scope.label}
                  {scope.name === "write_workbooks" && (
                    <span className="font-semibold italic">
                      {" "}
                      (requires read)
                    </span>
                  )}
                </label>
              ))}
            </div>
            <p className="text-sm text-[#595959]">
              Access is further limited by your account permissions. For
              example, if you can only view certain servers, this app will have
              the same restriction.
            </p>
          </>
        )}
        <div className="">
          You are authorizing access as:
          <div className="flex mt-1">
            <Avatar
              name={userFullname}
              avatarUrl={userAvatarUrl}
              className="flex"
            />
            <span className="ml-2">({userEmail})</span>
          </div>
        </div>
        <div>
          <input
            type="submit"
            value="Authorize"
            disabled={!hasOrg}
            className="btn btn-success w-full !p-[10px] !mt-4"
          />
        </div>
      </Form>
      <Form
        action={oauthAuthorization()}
        method="delete"
        className="!p-0 !mt-0"
      >
        <HiddenPreAuthFields preAuth={preAuth} />
        <div>
          <input
            type="submit"
            value="Deny"
            className="btn w-full !p-[10px] !font-medium !text-[16px] mt-3 bg-[#6b7280] text-white hover:bg-[#5b6370] hover:!text-white focus:bg-[#5b6370] focus:!text-white"
          />
        </div>
      </Form>
    </div>
  );
};

export default DoorkeeperAuthorization;
